import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import regionsTopoData from '../../../../Deepecho/constants/deployementData/geodata/regions.json'; 
import provincesTopoData from '../../../../Deepecho/constants/deployementData/geodata/provinces.json'; 
import { CENTERS } from '../../../../Deepecho/constants/deployementData/constant/constants';
import analyticsData from '../../../../Deepecho/constants/deployementData/analytics.json';

export const MoroccoMap = ({ onSelectCenter, activeId, regionalData, centerData }: any) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomRef = useRef<any>(null);
  const [view, setView] = useState<'national' | 'regional'>('national');
  const [selectedRegion, setSelectedRegion] = useState<any>(null);
  const [tooltip, setTooltip] = useState<any>(null);

  const regions = useMemo(() => topojson.feature(regionsTopoData as any, (regionsTopoData as any).objects.regions) as any, []);
  const provinces = useMemo(() => topojson.feature(provincesTopoData as any, (provincesTopoData as any).objects.provinces) as any, []);

  // Every region rendered in the same soft green tone, except Béni Mellal-Khénifra
  // (where our facilities live) which stays in a distinct blue so it stands out
  // as the region under focus. The green is muted enough that BMK still reads
  // as the dominant region.
  const REGION_NEUTRAL = '#BBF7D0';
  const BMK_BLUE = '#2563EB';
  const isBeniMellal = (name: string): boolean => {
    const n = (name || '').toLowerCase();
    return (
      n.includes('béni mellal') ||
      n.includes('beni mellal') ||
      n.includes('khénifra') ||
      n.includes('khenifra')
    );
  };

  const colorForRegionFeature = (feature: any): string => {
    const name = feature?.properties?.name || '';
    return isBeniMellal(name) ? BMK_BLUE : REGION_NEUTRAL;
  };

  // Give each province in the drilled-in region a distinguishable shade
  // derived from the parent region's hue (so provinces read as "same family,
  // different unit" rather than all identical).
  const shadeForProvince = (regionColor: string, index: number, total: number): string => {
    const base = d3.color(regionColor);
    if (!base) return regionColor;
    // Interpolate between a light and dark variant of the region hue.
    const light = d3.color(regionColor)!.brighter(1.4).formatHex();
    const dark = d3.color(regionColor)!.darker(0.6).formatHex();
    const t = total <= 1 ? 0.5 : index / (total - 1);
    return d3.interpolateLab(light, dark)(t);
  };

  const findData = (name: string) => {
    if (!name) return null;
    const cleanName = name.toLowerCase().replace(/province de |préfecture de /gi, "").trim();
    return regionalData?.find((r: any) =>
      r.region.toLowerCase().includes(cleanName) || cleanName.includes(r.region.split('-')[0].toLowerCase())
    );
  };

  const handleZoom = (type: 'in' | 'out' | 'reset') => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    if (type === 'reset') {
      svg.transition().duration(750).call(zoomRef.current.transform, d3.zoomIdentity);
    } else {
      svg.transition().duration(300).call(zoomRef.current.scaleBy, type === 'in' ? 1.5 : 0.7);
    }
  };

  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const width = 800, height = 900;
    svg.selectAll("*").remove();

    const g = svg.append("g");
    const zoomBehavior = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 10])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });
    zoomRef.current = zoomBehavior;
    svg.call(zoomBehavior);

    const projection = d3.geoMercator();
    const pathGenerator = d3.geoPath().projection(projection);

    let featuresToDraw: any[] = [];

    if (view === 'national') {
      featuresToDraw = regions.features;
      projection.fitExtent([[50, 50], [width - 50, height - 40]], regions);
    } else {
      featuresToDraw = provinces.features.filter((p: any) => {
        const centroid = d3.geoCentroid(p);
        return d3.geoContains(selectedRegion, centroid);
      });
      projection.fitExtent([[120, 120], [width - 120, height - 120]], { type: "FeatureCollection", features: featuresToDraw });
    }

    const parentColor = view === 'regional' ? colorForRegionFeature(selectedRegion) : null;
    g.selectAll(".map-path")
      .data(featuresToDraw)
      .enter()
      .append("path")
      .attr("d", pathGenerator)
      .attr("fill", (d: any, i: number) =>
        view === 'national'
          ? colorForRegionFeature(d)
          : shadeForProvince(parentColor!, i, featuresToDraw.length)
      )
      .attr("fill-opacity", view === 'national' ? 0.55 : 0.85)
      .attr("stroke", "#FFFFFF")
      .attr("stroke-width", view === 'national' ? 0.8 : 2)
      .attr("class", "cursor-pointer transition-all hover:opacity-80")
      .on("click", (e, d) => {
        if (view === 'national') {
          setSelectedRegion(d);
          setView('regional');
        }
      })
      .on("mousemove", (e, d: any) => {
        const data = findData(d.properties?.name) || (view === 'regional' ? findData(selectedRegion?.properties?.name) : null);
        const displayName = view === 'national'
          ? d.properties?.name
          : d.properties?.name?.replace(/province de |préfecture de |إقليم /gi, '').trim();
        setTooltip({
          type: view === 'national' ? 'Region' : 'Province',
          name: displayName,
          x: e.clientX,
          y: e.clientY,
          data,
        });
      })
      .on("mouseleave", () => setTooltip(null));

    if (view === 'regional') {
      g.selectAll(".province-label")
        .data(featuresToDraw)
        .enter()
        .append("text")
        .attr("class", "province-label pointer-events-none fill-white font-black uppercase")
        .attr("x", d => pathGenerator.centroid(d)[0])
        .attr("y", d => pathGenerator.centroid(d)[1])
        .attr("text-anchor", "middle")
        .style("font-size", "11px")
        .style("text-shadow", "0px 1px 3px rgba(0,0,0,0.4)")
        .text(d => d.properties?.name?.replace(/province de |إقليم /gi, "").trim() || "");
    }

    const visibleCenters = CENTERS.filter(c => {
        return view === 'national' ? true : d3.geoContains(selectedRegion, [c.lng, c.lat]);
    });

    const centersGroup = g.selectAll(".center-node")
      .data(visibleCenters)
      .enter()
      .append("g")
      .attr("class", "cursor-pointer")
      .on("click", (e, d) => {
        e.stopPropagation();
        onSelectCenter(d);
      })
      .on("mousemove", (e, d) => {
        const totalPatients =
          centerData?.[d.id]?.total_patients ??
          (analyticsData as any)[d.id]?.summary_kpis?.total_patients ??
          0;
        setTooltip({
          type: 'Centre Médical',
          name: d.name,
          x: e.clientX,
          y: e.clientY,
          data: { summary_kpis: { total_patients: totalPatients } },
          isCenter: true,
        });
      })
      .on("mouseleave", () => setTooltip(null));

    // Define a soft drop-shadow filter once for the markers.
    const defs = svg.append('defs');
    const filter = defs
      .append('filter')
      .attr('id', 'markerShadow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    filter
      .append('feDropShadow')
      .attr('dx', 0)
      .attr('dy', 1.5)
      .attr('stdDeviation', 2)
      .attr('flood-color', '#1E3A8A')
      .attr('flood-opacity', 0.35);

    const outerR = view === 'national' ? 12 : 20;
    const innerR = view === 'national' ? 5 : 8;

    // Soft halo behind the pin.
    centersGroup.append('circle')
      .attr('cx', d => projection([d.lng, d.lat])![0])
      .attr('cy', d => projection([d.lng, d.lat])![1])
      .attr('r', outerR)
      .attr('fill', d => (d.id === activeId ? '#10B981' : '#3B82F6'))
      .attr('fill-opacity', 0.18);

    // White ring.
    centersGroup.append('circle')
      .attr('cx', d => projection([d.lng, d.lat])![0])
      .attr('cy', d => projection([d.lng, d.lat])![1])
      .attr('r', innerR + 2.5)
      .attr('fill', '#FFFFFF')
      .attr('filter', 'url(#markerShadow)');

    // Solid inner dot.
    centersGroup.append('circle')
      .attr('cx', d => projection([d.lng, d.lat])![0])
      .attr('cy', d => projection([d.lng, d.lat])![1])
      .attr('r', innerR)
      .attr('fill', d => (d.id === activeId ? '#10B981' : '#2563EB'));

  }, [view, selectedRegion, regionalData, centerData, activeId, onSelectCenter, regions, provinces]);

  return (
    <div className="w-full h-full relative bg-white rounded-[2rem] overflow-hidden border border-slate-100 shadow-sm">
      
      {/* CONTRÔLES DE ZOOM */}
      <div className="absolute left-8 bottom-10 z-50 flex flex-col gap-2">
        <button onClick={() => handleZoom('in')} className="w-10 h-10 bg-white border border-slate-200 rounded-xl shadow-lg flex items-center justify-center font-bold text-slate-700 hover:bg-slate-50 transition-all"> + </button>
        <button onClick={() => handleZoom('out')} className="w-10 h-10 bg-white border border-slate-200 rounded-xl shadow-lg flex items-center justify-center font-bold text-slate-700 hover:bg-slate-50 transition-all"> − </button>
        <button onClick={() => handleZoom('reset')} className="w-10 h-10 bg-white border border-slate-200 rounded-xl shadow-lg flex items-center justify-center text-[9px] font-black uppercase text-blue-600 hover:bg-slate-50 transition-all"> RÉINIT </button>
      </div>

      {view === 'regional' && (
        <button 
          onClick={() => setView('national')}
          className="absolute top-8 left-8 z-50 bg-slate-900 text-white px-6 py-3 rounded-2xl text-[10px] font-bold shadow-2xl hover:bg-blue-600 transition-all flex items-center gap-2 uppercase tracking-widest"
        >
          ← Retour à la carte du Maroc
        </button>
      )}

      {/* Infos d'En-tête */}
      <div className="absolute top-24 right-10 text-right z-30 pointer-events-none">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-1">
          {view === 'national' ? 'National overview' : 'Province detail'}
        </p>
        <h2 className="text-xl md:text-2xl font-black text-slate-800 leading-tight">
          {view === 'national' ? 'Facilities across Morocco' : selectedRegion?.properties?.name}
        </h2>
      </div>

      <svg ref={svgRef} viewBox="0 0 800 900" className="w-full h-full cursor-grab active:cursor-grabbing" />
      
      {/* Tooltip Carte KPI */}
     {tooltip && !tooltip.isCenter && tooltip.name && (
      <div
        className="fixed pointer-events-none z-[9998] bg-slate-900/95 backdrop-blur-xl text-white px-4 py-2 rounded-xl shadow-2xl border border-white/10"
        style={{ left: tooltip.x + 16, top: tooltip.y + 16 }}
      >
        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">
          {tooltip.type}
        </div>
        <div className="text-xs font-black">{tooltip.name}</div>
      </div>
     )}

     {tooltip && tooltip.isCenter && (
      <div
        className="fixed pointer-events-none z-[9999] bg-slate-900/95 backdrop-blur-xl text-white p-5 rounded-[1.5rem] shadow-2xl min-w-[260px] border border-white/10"
        style={{ left: tooltip.x + 20, top: tooltip.y - 120 }}
      >
        <div className="flex justify-between items-center mb-4">
           <h4 className="font-black text-lg">{tooltip.name}</h4>
           <span className="bg-emerald-500/20 text-emerald-400 text-[8px] px-2 py-0.5 rounded-full font-bold border border-emerald-500/30 uppercase">Actif</span>
        </div>

        <div className="space-y-3 pt-3 border-t border-white/10">
          <div className="flex justify-between text-[11px]">
              <span className="text-slate-400">Total patients</span>
              <span className="font-black">
                {tooltip.data?.summary_kpis?.total_patients ?? 0}
              </span>
          </div>
        </div>
      </div>
    )}

      {/* Facilities legend */}
      <div className="absolute bottom-10 right-10 bg-white/90 backdrop-blur p-5 rounded-2xl border border-slate-200 shadow-sm max-w-[260px]">
        <p className="text-[10px] font-bold text-slate-400 uppercase mb-1 tracking-widest text-center">Facilities</p>
        <p className="text-[9px] font-medium text-slate-400 mb-3 text-center">
          # = patients at that facility
        </p>
        <div className="flex flex-col gap-2">
          {(CENTERS as any[]).map((c) => {
            const patients = centerData?.[c.id]?.total_patients;
            return (
              <div key={c.id} className="flex items-center gap-3">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-600 border-2 border-white shadow" />
                <span className="text-[11px] font-semibold text-slate-700 flex-1 truncate">
                  {c.name}
                </span>
                <span className="text-[10px] font-black text-slate-500">
                  {typeof patients === 'number' ? patients : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};